import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  CircularProgress,
} from "@mui/material";
import ConfirmUnitModal from "./ConfirmUnitModal";

/* ---------------- IMG PATH SETTINGS ---------------- */
const baseImgUrl =
  "https://pufucoawlndmgmkuflpx.supabase.co/storage/v1/object/public/floorplans/M15(Lavinto)/";

const REFUGE_FLOORS = [8, 13, 18, 23];
const REFUGE_SERIES = { A1: [], A2: ["03", "04"], B1: [], B2: ["03", "01"] };

function extractFloor(unitNo) {
  const raw = unitNo?.split("-")[1];

  if (!raw) return null;

  if (raw.length === 3) return parseInt(raw.charAt(0));
  if (raw.length === 4) return parseInt(raw.substring(0, 2));

  return parseInt(raw.slice(0, raw.length - 2));
}

/* ---------- DYNAMIC IMAGE HANDLER ---------- */
function getFloorPlanImage(unit) {
  if (!unit) return "";

  const cleanWing = unit.unitNo.split("-")[0].toUpperCase();
  const cleanType = unit.displayUnitType
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  const cleanArea = Math.floor(unit.chargeableArea * 10.76) + 1;

  const unitNum = unit.unitNo.split("-")[1];
  const floor = extractFloor(unit.unitNo);
  const series = unitNum.slice(-2);

  const isRefugeFloor = REFUGE_FLOORS.includes(floor);
  const isRefugeSeries = (REFUGE_SERIES[cleanWing] || []).includes(series);

  let fileName = `${cleanWing}_${cleanType}_${cleanArea}_${series}.png`;

  if (isRefugeFloor && isRefugeSeries) {
    fileName = `${cleanWing}_${cleanType}_${cleanArea}_${series}_refuge.png`;
  }

  return `${baseImgUrl}${fileName}`;
}

/* ---------------- MAIN UI ---------------- */
export default function UnitDetails() {
  const navigate = useNavigate();
  const location = useLocation();

  const [unitDetails, setUnitDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);

  let { paymentId, tokenNo, selectedUnit } = location.state || {};
  const [unitId, setUnitId] = useState(selectedUnit?.unitId?.unitId || null);

  const [isModalOpen, setIsModalOpen] = useState(false);

  /* -------- FETCH UNIT DETAILS -------- */
  useEffect(() => {
    async function fetchDetails() {
      setIsLoading(true);
      try {
        const { data } = await axios.post(
          `${process.env.REACT_APP_PAYMENT_LINK_URL}customerOnlineBooking/getDetailsOfSingleUnit`,
          { unitId }
        );

        if (data.success && data.data) {
          const raw = data.data;

          const formatted = {
            unitId: raw.unit.unitId,
            unitNo: raw.unit.unitNo,
            saleableArea: raw.unit.saleableArea,
            displayUnitType: raw.unit.displayUnitType,
            wingName: raw.unit.wingId.displayName,
            unitType: raw.unit.inventoryTypeId.type.replace(".00", ""),
            title: raw.launch.event.title,
            floorNo: raw.unit.floorNo,
            description: raw.launch.event.description,
            displayName: raw.unit.wingId.displayName,
            chargeableArea: raw.unit.chargeableArea,
            totalCost: raw.unit.totalCost || 0,
            inventoryStatusId: raw.unit.inventoryStatusId,
            leadId: raw.unit.leadId,
          };

          setUnitDetails(formatted);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }

    if (unitId) fetchDetails();
  }, []);

  /* -------- SHOW GLOBAL LOADER -------- */
  if (isLoading || !unitDetails)
    return (
      <Typography style={{ textAlign: "center", padding: 40, fontSize: 20 }}>
        {/* Loading Unit Details... */}
        <CircularProgress />
      </Typography>
    );

  const imgUrl = getFloorPlanImage(unitDetails);

  return (
    <Box
      sx={{
        padding: { xs: "5px", md: "10px" },
        maxWidth: "1300px",
        margin: "0 auto",
        overflowY: "auto",
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          marginBottom: 3,
        }}
      >
        <Box
          onClick={() => navigate("/Home/Tokens")}
          sx={{
            display: "inline-block",
            fontSize: { xs: 14, sm: 14 },
            fontWeight: 600,
            textTransform: "uppercase",
            background: "linear-gradient(90deg, #0072ff 0%, #00b4e6ff 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            cursor: "pointer",
            position: "relative",
            letterSpacing: 0.5,
            "&::after": {
              content: '""',
              position: "absolute",
              left: 0,
              bottom: -3,
              width: "100%",
              height: 2,
              background: "linear-gradient(90deg, #0072ff, #00b4e6ff)",
              transform: "scaleX(0)",
              transformOrigin: "right",
              transition: "transform 0.3s ease",
            },
            "&:hover::after": {
              transform: "scaleX(1)",
              transformOrigin: "left",
            },
          }}
        >
          ← Go Back
        </Box>

        <Typography sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 700 }}>
          Unit Details
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* LEFT — FLOOR PLAN */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
              position: "relative",
            }}
          >
            {isImageLoading && (
              <Typography
                sx={{
                  textAlign: "center",
                  padding: 3,
                  fontWeight: 600,
                }}
              >
                Loading Image...
              </Typography>
            )}

            <img
              src={imgUrl}
              alt="Floor Plan"
              onLoad={() => setIsImageLoading(false)}
              onError={() => setIsImageLoading(false)}
              style={{
                width: "100%",
                height: "auto",
                display: isImageLoading ? "none" : "block",
              }}
            />
          </Card>
        </Grid>

        {/* RIGHT — DETAILS */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              borderRadius: 4,
              padding: 2,
              boxShadow: "0 6px 20px rgba(0,0,0,0.10)",
              background: "rgba(255,255,255,0.7)",
              backdropFilter: "blur(10px)",
            }}
          >
            <CardContent>
              <Typography
                sx={{
                  fontSize: 26,
                  fontWeight: 700,
                  marginBottom: 2,
                }}
              >
                 <strong>Unit No: </strong>
                {unitDetails.unitNo}
              </Typography>

         <Box sx={{ marginBottom: 2 }}>
  {[
    { label: "Wing", value: unitDetails.wingName },
    { label: "Type", value: unitDetails.unitType },
    { label: "Saleable Area", value: `${unitDetails.saleableArea} sq.ft` },
    { label: "Carpet Area", value: `${Math.floor(unitDetails.chargeableArea * 10.76)} sq.ft` },
    { label: "Floor", value: unitDetails.floorNo },
  ].map((item, index) => (
    <Box
      key={index}
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        // justifyContent: "space-between",
        fontSize: 20,
        marginBottom: 1.2,
      }}
    >
      <Typography sx={{ fontWeight: 600,fontSize: 18 }}>{item.label}:</Typography>
      <Typography sx={{ fontSize: 18 }}>{item.value}</Typography>
    </Box>
  ))}
</Box>


              <Divider sx={{ my: 2 }} />

              <Button
                variant="contained"
                sx={{
                  padding: "12px 32px",
                  borderRadius: 3,
                  fontSize: 16,
                  width: "100%",
                  fontWeight: 600,
                  background:
                    "linear-gradient(135deg, #0052D4, #4364F7, #4197faff)",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                }}
                onClick={() => setIsModalOpen(true)}
              >
                Proceed
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ACTION BUTTONS */}
      {/* <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: 4,
                    gap: 2
                }}
            >
       
            </Box> */}

      {isModalOpen && (
        <ConfirmUnitModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          unitData={unitDetails}
          paymentId={paymentId}
          tokenNo={tokenNo}
        />
      )}
    </Box>
  );
}
